import { createSelector } from 'reselect'
import { handle } from 'redux-pack'
import { getConfig } from 'config'
import { urls } from '../urls'
import { acceptInvite } from 'remote/invites'

import { AcceptInvitationSteps } from './AcceptInvitationSteps'
import { SET_INVITE_TOKEN, SET_USER_INFORMATIONS } from '../Join/actions'
import { clearSessionSafe } from 'helpers/localStorage'

const INITIAL_STATE = {
  step: AcceptInvitationSteps.UserStep,

  user: {
    displayName: '',
    password: '',
  },
  invite: null,
  organization: null,
  authTokens: null,

  fetchInvite: {
    pending: false,
    error: null,
    result: null,
  },

  acceptInvite: {
    pending: false,
    error: null,
    result: null,
  },
}

// selectors
export const getJoinState = (state) => state.organization.accept
export const getStep = createSelector(getJoinState, ({ step }) => step)
export const getUser = createSelector(getJoinState, ({ user }) => {
  return user
})
export const getInvite = createSelector(getJoinState, ({ invite }) => invite)
export const getInviteToken = createSelector(
  getJoinState,
  ({ inviteToken }) => inviteToken
)
export const getOrganizationId = createSelector(
  getJoinState,
  ({ organizationId }) => organizationId
)
const getOrganization = createSelector(
  getJoinState,
  ({ organization }) => organization || {}
)

const getAuthTokens = createSelector(
  getJoinState,
  ({ authTokens }) => authTokens
)
export const getErrors = createSelector(
  getJoinState,
  ({ fetchInvite, acceptInvite }) => ({
    fetch: fetchInvite.error,
    accept: acceptInvite.error,
  })
)

export const getAutoSignInPath = createSelector(
  getConfig,
  getOrganization,
  getAuthTokens,
  (config, { domain }, tokens) =>
    tokens && urls.getAutoSignUrl(config, domain, tokens)
)

export function init({ inviteToken, displayName, organizationId }) {
  return (dispatch, getState, config) => {
    dispatch({
      type: SET_INVITE_TOKEN,
      payload: {
        inviteToken,
        organizationId,
      },
    })
    if (displayName) {
      dispatch({
        type: SET_USER_INFORMATIONS,
        payload: {
          displayName,
        },
      })
    }
  }
}

export function setUserCredentials(userInformations) {
  return {
    type: 'ORGANIZATION/JOIN/SET_USER_INFORMATIONS',
    payload: {
      step: AcceptInvitationSteps.UserStep,
      ...userInformations,
    },
  }
}

export function setUserPositionInformations(userPositionInformations) {
  return (dispatch, getState) => {
    const user = getUser(getState())

    dispatch({
      type: 'ORGANIZATION/JOIN/SET_USER_POSITION_INFOS',
      payload: {
        user: {
          ...user,
          ...userPositionInformations,
        },
      },
    })
    dispatch(submit())
  }
}

// action creators
export function submit() {
  return (dispatch, getState) => {
    const { displayName, password } = getUser(getState())
    const inviteToken = getInviteToken(getState())
    const organizationId = getOrganizationId(getState())
    const user = { displayName, password }

    return dispatch({
      type: 'ORGANIZATION/JOIN/ACCEPT_INVITE',
      payload: { user, inviteToken },
      promise: acceptInvite(organizationId, inviteToken, user),
    })
  }
}

export default function reducer(state = INITIAL_STATE, action) {
  const { type, payload } = action

  switch (type) {
    case SET_INVITE_TOKEN:
      return {
        ...state,
        inviteToken: payload.inviteToken,
        organizationId: payload.organizationId,
        step: AcceptInvitationSteps.UserStep,
      }

    case SET_USER_INFORMATIONS:
      const { step, ...userInformations } = payload
      return {
        ...state,
        step: payload.step,
        user: { ...state.user, ...userInformations },
      }

    //TODO: make sure this is still useful
    case 'ORGANIZATION/JOIN/ACCEPT_INVITE':
      return handle(state, action, {
        start: (s) => ({
          ...s,
          user: {
            ...state.user,
            ...payload.user,
          },
          step: AcceptInvitationSteps.Creating,
          acceptInvite: {
            pending: true,
            result: null,
            error: null,
          },
        }),
        failure: (s) => ({
          ...s,
          acceptInvite: {
            ...s.acceptInvite,
            error: payload,
          },
        }),
        success: (s) => ({
          ...s,
          acceptInvite: {
            ...s.acceptInvite,
            result: payload,
          },
          authTokens: payload.authTokens,
        }),
        finish: (s) => {
          clearSessionSafe()
          return {
            ...s,
            step: AcceptInvitationSteps.Finished,
            acceptInvite: {
              ...s.acceptInvite,
              pending: false,
            },
          }
        },
      })

    case 'ORGANIZATION/JOIN/CHANGE_STEP': {
      const { step } = payload
      return {
        ...state,
        step,
      }
    }

    case 'ORGANIZATION/JOIN/SET_USER_POSITION_INFOS': {
      return {
        ...state,
        ...payload,
      }
    }

    default:
      return state
  }
}
